import React from 'react';

interface ContractSigningInfoCard {
  icon: string;
  title: string;
  text: string;
}

interface ContractSigningActionsProps {
  description: string;
  signApproveLabel: string;
  reviewInfoCards: ContractSigningInfoCard[];
  /** Heading over the info steps, e.g. "Så går det till". */
  reviewInfoTitle?: string;
  isSignDisabled: boolean;
  isOpeningSigning: boolean;
  openingSigningLabel: string;
  onCompanySign: () => void;
}

/**
 * How review and signing work, as numbered steps in plain text — not cards,
 * which read as buttons. Folded up and placed last (ClickUp 869fmxfb7): the
 * state and the next step come first.
 */
export function ContractSigningHelp({ description, reviewInfoTitle, reviewInfoCards }: Pick<ContractSigningActionsProps,
  'description' | 'reviewInfoTitle' | 'reviewInfoCards'>) {
  return (
    <details className="contract-signing-help">
      <summary>
        <i className="feather-info" aria-hidden="true"></i>
        <span className="contract-signing-help-title">{reviewInfoTitle}</span>
        <i className="feather-chevron-down contract-signing-help-chevron" aria-hidden="true"></i>
      </summary>
      <p className="contract-actions-text">{description}</p>
      <section className="contract-signing-info" data-testid="contract-signing-info" aria-label={reviewInfoTitle}>
        <ol className="contract-signing-info-steps">
          {reviewInfoCards.map((card, index) => (
            <li key={card.title}>
              <span className="contract-signing-info-number" aria-hidden="true">{index + 1}</span>
              <span>
                <span className="contract-signing-info-step-title">{card.title}</span>
                <span className="contract-signing-info-step-text">{card.text}</span>
              </span>
            </li>
          ))}
        </ol>
      </section>
    </details>
  );
}

type ContractSigningButtonsProps = Pick<ContractSigningActionsProps,
  'signApproveLabel' | 'isSignDisabled' | 'isOpeningSigning' | 'openingSigningLabel' | 'onCompanySign'>;

/** The company's signing, as a button for the contract list's actions row
 * (ClickUp 869fmxfb7). "Förhandsgranska" sits next to it, for every contract
 * not yet signed by both. */
export function ContractSigningButtons({
  signApproveLabel, isSignDisabled, isOpeningSigning, openingSigningLabel, onCompanySign
}: ContractSigningButtonsProps) {
  return (
    <span className="contract-list-signing-actions" data-testid="company-signing-actions">
      <button type="button" className="btn btn-sm contract-list-action is-primary" onClick={onCompanySign} disabled={isSignDisabled || isOpeningSigning}>
        <i className="feather-check-square" aria-hidden="true"></i>{isOpeningSigning ? openingSigningLabel : signApproveLabel}
      </button>
    </span>
  );
}
